/**
 * Cryptographically secure random number generation utilities
 * Uses Web Crypto API to avoid SonarQube security warnings about Math.random()
 */

/**
 * Generates a cryptographically secure random integer in a given range [min, max)
 * @param min Minimum value (inclusive)
 * @param max Maximum value (exclusive)
 * @returns Random integer between min and max
 */
export function getSecureRandomInt(min: number, max: number): number {
  const randomBuffer = new Uint32Array(1);
  crypto.getRandomValues(randomBuffer);
  const range = max - min;
  return Math.floor((randomBuffer[0] / 0xffffffff) * range) + min;
}

/**
 * Generates a cryptographically secure random index for array selection
 * @param arrayLength Length of the array
 * @returns Random index between 0 and arrayLength-1
 */
export function getSecureRandomIndex(arrayLength: number): number {
  const randomBuffer = new Uint32Array(1);
  crypto.getRandomValues(randomBuffer);
  return randomBuffer[0] % arrayLength;
}

/**
 * Generates a cryptographically secure random jitter value between -1 and 1
 * Useful for adding randomness to delays to prevent thundering herd problems
 * @returns Random number between -1 and 1
 */
export function getSecureRandomJitter(): number {
  const randomBuffer = new Uint32Array(1);
  crypto.getRandomValues(randomBuffer);
  return (randomBuffer[0] / 0xffffffff) * 2 - 1;
}

/**
 * Generates a cryptographically secure random number between 0 and 1
 * @returns Random number between 0 and 1
 */
export function getSecureRandom(): number {
  const randomBuffer = new Uint32Array(1);
  crypto.getRandomValues(randomBuffer);
  return randomBuffer[0] / 0xffffffff;
}
